import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useNotifications } from 'app/application/application'
import { Form } from 'app/common/presentation'
import { createTrackingScript } from 'app/connections/application/innkeepr'
import {
  InnkeeprConnection,
  InstalledInnkeeprConnection,
  isInstalledConnection,
} from 'app/connections/types'
import { useUser } from 'app/user/application'

import { Button, Input } from 'components'

import { SettingsProps } from '../Settings'
import { DangerZone, SettingsSection } from '../components'
import styles from './InnkeeprSettings.module.scss'

interface InnkeeprSettingsProps extends SettingsProps {
  onTrackingUrlUpdate: (trackingUrl: string) => void
  connection: InnkeeprConnection | InstalledInnkeeprConnection
  trackingUrl: string
}

const trackingUrlKey = 'innkeepr_tracking_url'

const InnkeeprSettings = ({
  onConnectionConfigValidationUpdate,
  onTrackingUrlUpdate,
  connection,
  trackingUrl: incomingTrackingUrl,
}: InnkeeprSettingsProps) => {
  const { t } = useTranslation('connections')
  const { t: tCommon } = useTranslation('common')
  const { createNotification } = useNotifications()
  const { workspace } = useUser()
  const [trackingUrl, setTrackingUrl] = useState(
    (incomingTrackingUrl || localStorage.getItem(trackingUrlKey)) ?? '',
  )
  const isInstalled = isInstalledConnection(connection)

  useEffect(() => {
    if (isInstalled) {
      localStorage.removeItem(trackingUrlKey)
    } else {
      localStorage.setItem(trackingUrlKey, trackingUrl)
    }
  }, [isInstalled, trackingUrl])

  useEffect(() => {
    onTrackingUrlUpdate(trackingUrl)
    onConnectionConfigValidationUpdate(trackingUrl.trim().length > 0)
  }, [onConnectionConfigValidationUpdate, onTrackingUrlUpdate, trackingUrl])

  const script = workspace ? createTrackingScript(workspace.apiKey, trackingUrl, false) : ''

  const copyScript = async () => {
    await navigator.clipboard.writeText(script)
    createNotification({
      message: tCommon('copy.copySuccessful.message', { what: 'Script' }),
      title: tCommon('copy.copySuccessful.title'),
      type: 'success',
    })
  }

  if (isInstalled) {
    return (
      <>
        <SettingsSection
          contentClassName={styles.installCard}
          description={t('setup.innkeepr.install.description')}
          title={t('setup.innkeepr.install.title')}
        >
          <div className={styles.scriptRow}>
            <Input className={styles.scriptInput} value={script.replace(/\s+/g, ' ').trim()} />
            <Button disabled={!script} onClick={() => void copyScript()} variant="secondary">
              {tCommon('copy.title')}
            </Button>
          </div>
        </SettingsSection>
        {<DangerZone connection={connection} />}
      </>
    )
  }

  return (
    <>
      <SettingsSection
        contentClassName={styles.basicCard}
        description={t('setup.basics.description')}
        title={t('setup.basics.title')}
      >
        <Form.InputFormRow
          description={t(`setup.${connection.name}.config.trackingUrl.description`)}
          onChange={setTrackingUrl}
          placeholder={t(`setup.${connection.name}.config.trackingUrl.title`)}
          title={t(`setup.${connection.name}.config.trackingUrl.title`)}
          value={trackingUrl}
        />
      </SettingsSection>
    </>
  )
}

export default InnkeeprSettings
